/* JAMBHALA paired-image controller
 * Loads each second (jewelry) image only shortly before its card enters view.
 * Starts the 12s crossfade after both photos finish loading/decoding.
 * Offscreen pairs pause. Static Being images remain if a request fails.
 */
(() => {
  "use strict";

  const INIT_KEY = "__jbPairedImageLoaderInitialized";
  if (window[INIT_KEY]) return;
  window[INIT_KEY] = true;

  const boot = () => {
    const productImages = Array.from(document.querySelectorAll("img[data-jb-swap-src]"));
    if (!productImages.length) return;

    const motionReduced = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (motionReduced || (connection && connection.saveData)) return;

    const pairs = [];

    const decoded = (image) => {
      const waitLoaded = image.complete
        ? image.naturalWidth > 0 ? Promise.resolve() : Promise.reject(new Error("Image failed"))
        : new Promise((resolve, reject) => {
            image.addEventListener("load", resolve, { once: true });
            image.addEventListener("error", reject, { once: true });
          });
      return waitLoaded.then(() => (
        typeof image.decode === "function"
          ? image.decode().catch(() => {
              if (image.naturalWidth === 0) throw new Error("Image decoding failed");
            })
          : Promise.resolve()
      ));
    };

    for (const product of productImages) {
      const wrapper = product.closest(".blog-post-item, .jb-being-swap, .jb-bai-ze-swap");
      if (!wrapper) continue;
      const cover = wrapper.querySelector(".blog-post-card__image") ||
        Array.from(wrapper.querySelectorAll("img")).find(image => image !== product);
      if (!cover) continue;
      wrapper.classList.add("jb-image-pair");
      pairs.push({ wrapper, product, cover, requested: false, visible: false });
    }
    if (!pairs.length) return;

    const loadNear = pair => {
      if (pair.requested) return;
      pair.requested = true;
      const { product, cover, wrapper } = pair;
      if (!product.dataset.jbSwapSrc) return;

      // First finish the visible Being image; prevent extra jewelry requests
      // from competing with primary content on slow mobile connections.
      decoded(cover).then(() => {
        if (!wrapper.isConnected) return;
        product.loading = "eager";
        product.fetchPriority = "low";
        if (product.dataset.jbSwapSrcset) product.srcset = product.dataset.jbSwapSrcset;
        product.src = product.dataset.jbSwapSrc;
        return decoded(product).then(() => {
          if (wrapper.isConnected) wrapper.classList.add("jb-image-pair--ready");
        });
      }).catch(() => {
        // Keep the original Being image visible if either image is unavailable.
      });
    };

    const syncVisibility = pair => {
      pair.wrapper.classList.toggle(
        "jb-image-pair--visible",
        pair.visible && document.visibilityState === "visible"
      );
    };

    // A smaller mobile prefetch window avoids exhausting scarce bandwidth.
    const mobile = window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
    const preloadDistance = mobile ? 320 : 650;

    if ("IntersectionObserver" in window) {
      const prefetch = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const pair = pairs.find(p => p.wrapper === entry.target);
          if (pair) loadNear(pair);
          prefetch.unobserve(entry.target);
        }
      }, { rootMargin: preloadDistance + "px 0px", threshold: 0 });

      const visibility = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const pair = pairs.find(p => p.wrapper === entry.target);
          if (!pair) continue;
          pair.visible = entry.isIntersecting;
          syncVisibility(pair);
        }
      }, { threshold: 0.01 });

      for (const pair of pairs) {
        prefetch.observe(pair.wrapper);
        visibility.observe(pair.wrapper);
      }
    } else {
      // Old browser fallback: check nearby cards without downloading all 82 photos.
      let queued = false;
      const update = () => {
        queued = false;
        for (const pair of pairs) {
          const bounds = pair.wrapper.getBoundingClientRect();
          pair.visible = bounds.bottom > 0 && bounds.top < innerHeight;
          syncVisibility(pair);
          if (bounds.bottom > -preloadDistance && bounds.top < innerHeight + preloadDistance) loadNear(pair);
        }
      };
      const requestUpdate = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      };
      window.addEventListener("scroll", requestUpdate, { passive: true });
      window.addEventListener("resize", requestUpdate);
      update();
    }

    document.addEventListener("visibilitychange", () => {
      for (const pair of pairs) syncVisibility(pair);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
